import { defineStore } from 'pinia';
import { base64encode } from '@/utils';

// 创建路由id
const createRouteId = (route) => {
    const { path, query } = route || {};
    if (!path) return '';
    const queryFields = [];
    for (const k in query) {
        queryFields.push(k);
    }
    // 字段排序
    queryFields.sort((a, b) => {
        if (a > b) return -1;
        else if (a < b) return 1;
        return 0;
    });
    let queryStr = '';
    for (const k of queryFields) {
        if (queryStr) queryStr += '&';
        queryStr += `${k}=${query[k]}`;
    }
    const str = `${path}?${queryStr}`;
    return base64encode(str);
};

export const createUseVisitedViewStore = (storageKey = 'ijx-visited-view-states') => defineStore('visitedViewStore', {
    id: 'visitedViewStore',
    state: () => ({
        visitedViews: {},
        activeView: {},
    }),
    getters: {
        groupViews (state) {
            return group => state.visitedViews[group];
        },
        groupActiveView (state) {
            return group => state.activeView[group];
        },
    },
    actions: {
        addView (view) {
            const { name = '', path, params = {}, query = {}, meta } = view || {};
            const decPath = decodeURI(path);
            const {
                title = '未设置标题',
                closable = true,
                group = '',
            } = meta || {};

            const routeId = createRouteId(view);

            this.activeView[group] = path;

            let items = this.visitedViews[group] || [];
            if (!items || !Array.isArray(items)) {
                items = [];
            }
            const oldIdx = items.findIndex(v => v.path === decPath);
            if (oldIdx !== -1) {
                items[oldIdx].id = routeId;
                items[oldIdx].query = query;
                this.visitedViews[group] = items;
                return;
            }
            items.push({
                id: routeId,
                name,
                path: decPath,
                params,
                query,
                closable,
                title,
                group,
            });
            this.visitedViews[group] = items;
        },
        delView (view) {
            const group = view.meta.group ?? '';
            const groupVisitViews = this.visitedViews[group] || [];
            for (const [ i, v ] of groupVisitViews.entries()) {
                if (v.path === view.path) {
                    groupVisitViews.splice(i, 1);
                    if (i < groupVisitViews.length) {
                        this.activeView[group] = groupVisitViews[i].path;
                    } else {
                        if (i - 1 > 0) {
                            this.activeView[group] = groupVisitViews[i - 1].path;
                        } else if (groupVisitViews.length > 0) {
                            this.activeView[group] = groupVisitViews[0].path;
                        } else {
                            this.activeView[group] = '';
                        }
                    }
                    this.visitedViews[group] = groupVisitViews;
                    return i;
                }
            }
            return -1;
        },
        clearGroupViews (group) {
            this.$patch(state => {
                state.visitedViews[group] = [];
                state.activeView[group] = '/';
            });
        },
        'delGroupView2' (group, view) {
            const views = this.visitedViews[group] || [];
            const newViews = [ ...views ];
            let newActiveKey = this.activeView[group] || '';
            let nextView = {};
            for (const [ i, v ] of views.entries()) {
                if (v.path === view.path) {
                    newViews.splice(i, 1);
                    if (i < newViews.length) {
                        nextView = newViews[i];
                        newActiveKey = nextView.path;
                    } else {
                        const nextIdx = i - 1;
                        if (nextIdx >= 0 && nextIdx < newViews.length) {
                            nextView = newViews[nextIdx];
                            newActiveKey = nextView.path;
                        } else {
                            nextView = { path: '/' };
                            newActiveKey = '';
                        }
                    }
                    this.$patch(state => {
                        state.visitedViews[group] = newViews;
                        state.activeView[group] = newActiveKey;
                    });
                    return { index: i, nextView };
                }
            }
            this.$patch(state => {
                state.visitedViews[group] = newViews;
                state.activeView[group] = '/';
            });
            return { index: -1, nextView: { path: '/' } };
        },
        delGroupView (group, view) {
            const views = this.visitedViews[group] || [];
            const newViews = [ ...views ];
            let newActiveKey = this.activeView[group] || '';
            for (const [ i, v ] of views.entries()) {
                if (v.path === view.path) {
                    newViews.splice(i, 1);
                    if (i < newViews.length) {
                        newActiveKey = newViews[i].path;
                    } else {
                        const nextIdx = i - 1;
                        if (nextIdx >= 0 && nextIdx < newViews.length) {
                            newActiveKey = newViews[nextIdx].path;
                        } else {
                            newActiveKey = '';
                        }
                    }
                    this.$patch(state => {
                        state.visitedViews[group] = newViews;
                        state.activeView[group] = newActiveKey;
                    });
                    return i;
                }
            }
            this.$patch(state => {
                state.visitedViews[group] = newViews;
                state.activeView[group] = '/';
            });
            return -1;
        },

        /**
         * 删除除了参数view外的其他视图
         * @param view
         */
        delOtherView (view) {
            const group = view.meta.group ?? '';
            this.visitedViews[group] = [
                {
                    name: view.name ?? '',
                    path: view.path,
                    params: view.params ?? {},
                    query: view.query ?? {},
                    title: view.meta.title ?? '',
                    closable: view.meta.closable ?? true,
                    group: view.meta.group ?? '', // 分组
                },
            ];
            this.activeView[group] = view.path;
        },

        /**
         * 删除所有视图
         */
        'delAllView' () {
            this.$patch((state) => {
                state.visitedViews = {};
                state.activeView = {};
            });
        },

        /**
         * 删除指定分组视图
         * @param group
         */
        delGroupAllView (group) {
            this.$patch((state) => {
                state.visitedViews[group] = [];
                state.activeView[group] = '/';
            });
        },

        /**
         * 更改当前视图标题
         * @param view
         * @param title
         */
        'setTitle' (view, title) {
            const { path, meta } = view;
            const group = meta.group ?? '';
            const decPath = decodeURI(path);
            const oldView = (this.visitedViews[group] || []).find(v => v.path === decPath);
            if (oldView) {
                oldView.title = title;
            }
        },

        'setActiveView' (group, path) {
            this.$patch((state) => {
                state.activeView[group] = path;
            });
        },
    },
    persist: {
        key: storageKey,
        storage: localStorage,
    },
});

export const useVisitedViewStore = () => {
    return createUseVisitedViewStore('ijx-visited-view-states')();
};

